import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, SecurityContext, ViewChild, ViewEncapsulation } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

import { marked } from 'marked';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { User } from '@common/models';
import { AuthenticationService } from '@common/services';

import { InsightsService, MSGraphService } from '@core/services';
import { blobToBase64 } from '@core/utils';

import { CustomSwalService } from 'app/helpers';

import { environment } from 'environments/environment';

import { ChatAgentOption, ChatAgentService } from '../services/chat-agent.service';
import { ChatQuoteModalComponent } from '../chat-quote-modal/chat-quote-modal.component';
import { ChatFilePreviewModalComponent } from '../chat-file-preview-modal/chat-file-preview-modal.component';
import { ChatDockService } from '../services/chat-dock.service';
import { ChatSessionService } from '../services/chat-session.service';
import { AppError, ChatMessage, ChatSummary, ReasoningStep } from '../types';
import { convertFilesToDataUris, FileConversionResult, MAX_FILE_COUNT, validateFileCount } from '../utils';

@Component({
  selector: 'app-chat-content',
  templateUrl: './chat-content.component.html',
  styleUrls: ['./chat-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
  host: { class: 'chat-application' },
  standalone: false
})
export class ChatContentComponent implements OnInit, OnDestroy {

  // ─── Conversación ───────────────────────────────────────────────────
  //
  // El hilo vive en ChatSessionService para que sobreviva a esta vista: el
  // componente solo lo expone a la plantilla.

  /** Hilo completo de la conversación */
  get messages(): ChatMessage[] { return this._session.messages; }
  /** Conversación viva en el agente, la devuelve el propio stream */
  get conversationId(): string | null { return this._session.conversationId; }
  /** Estado del turno actual */
  get status(): 'idle' | 'sending' | 'streaming' | 'error' { return this._session.status; }
  /** Error estructurado del último turno, si lo hubo */
  get error(): AppError | null { return this._session.error; }

  /** Archivos convertidos a data uri, pendientes de enviarse con el próximo mensaje */
  get pendingFiles(): FileConversionResult[] { return this._session.pendingFiles; }
  /** Ya no caben más adjuntos en el mensaje actual */
  get attachLimitReached(): boolean { return this.pendingFiles.length >= MAX_FILE_COUNT; }
  set pendingFiles(value: FileConversionResult[]) { this._session.pendingFiles = value; }

  public currentUser: User;
  public profilePhoto: any;

  public bixAvatar: string = 'https://belivthumbs.blob.core.windows.net/prod/bot.png';

  /** Texto que el usuario está escribiendo */
  public draft: string = '';
  /** Ancho actual del panel acoplado, en % del viewport */
  public dockWidth: number;
  /** Presets de ancho ofrecidos en la cabecera cuando está acoplado */
  public readonly widthPresets: number[] = [30, 40, 50];

  // ─── Historial ──────────────────────────────────────────────────────

  /** Conversaciones guardadas del usuario, de la más reciente a la más antigua */
  get history(): ChatSummary[] { return this._session.history; }
  /** Conversación abierta en este momento, se corresponde con una llave del storage */
  get activeChatId(): string | null { return this._session.activeChatId; }
  /** Se está leyendo el índice o un hilo del storage */
  get historyLoading(): boolean { return this._session.historyLoading; }
  /** El panel de conversaciones está desplegado. Solo existe en pantalla completa */
  public historyOpen: boolean = true;

  /** Mensaje que se acaba de copiar, para confirmarlo en su propio botón */
  public copiedMessageId: string | null = null;

  /** Lado máximo de la captura de pantalla, en píxeles */
  private readonly SCREENSHOT_MAX_SIDE = 1600;

  @Input() isFloating: boolean;
  /**
   * Contexto en el que se pinta el chat:
   * - `page`  → página completa (/pages/chat)
   * - `dock`  → panel lateral acoplado que encoge la app
   * - `float` → burbuja flotante heredada (Belivpedia)
   */
  @Input() variant: 'page' | 'dock' | 'float' = 'page';

  @Output() hideChat: EventEmitter<boolean> = new EventEmitter();

  // Decorator
  @ViewChild('scrollMe') scrollMe: ElementRef;
  scrolltop: number = null;

  /** Evita tocar la vista después de destruir el componente */
  private destroyed = false;

  /** Hay un ajuste de scroll ya agendado, no hace falta encolar otro */
  private scrollPending = false;

  private _unsubscribeAll: Subject<any>;

  /**
   * Constructor
   * @param {AuthenticationService} _authenticationService Provee el token con el que se llama al agente
   * @param {CustomSwalService} _swalService
   * @param {MSGraphService} _msGraphService
   * @param {NgbModal} _ngbModalService
   * @param {ChatSessionService} _session Conversación activa y stream contra el agente Bryant
   * @param {TranslateService} _translateService
   * @param {Router} _router
   * @param {InsightsService} _log
   * @param {ChatDockService} _dockService
   * @param {ChatAgentService} _agentService Despliegue del agente contra el que se habla
   * @param {ChangeDetectorRef} _cdr
   */
  constructor(private _authenticationService: AuthenticationService,
    private _swalService: CustomSwalService,
    private _msGraphService: MSGraphService,
    private _ngbModalService: NgbModal,
    private _session: ChatSessionService,
    private _translateService: TranslateService,
    private _router: Router,
    private _log: InsightsService,
    private _dockService: ChatDockService,
    private _agentService: ChatAgentService,
    private _sanitizer: DomSanitizer,
    private _cdr: ChangeDetectorRef) {
    this._unsubscribeAll = new Subject();
  }

  /** Correo del usuario, es la primera parte de la llave del storage */
  private get email(): string {
    return this._session.email;
  }

  // ─── Ciclo de vida ──────────────────────────────────────────────────

  /**
   * OnInit
   */
  ngOnInit(): void {

    // Cuando se detecte que hay un valor del usuario se procederá a cargar la imagen del usuario
    this._authenticationService.onUserValueChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(resp => {
      if (resp) {
        // Obtiene el usuario actual
        this.currentUser = this._authenticationService.currentUserValue;

        // El historial vive bajo el correo del usuario, así que recién acá se
        // conoce la llave con la que hay que leerlo. La sesión lo carga una
        // sola vez aunque se monten varias vistas del chat
        this._session.connect(this.currentUser?.email);

        // Si la autenticación es por medio del active directory
        if (localStorage.getItem('auth-type') === 'saml') {
          // Registra el servicio para obtener el token
          this._msGraphService.register();
        }
      }
    });

    // Espera hasta obtener el token de acceso para msal graph
    this._msGraphService.onAccessTokenChanged.pipe(takeUntil(this._unsubscribeAll)).subscribe(response => {
      if (response) {
        // Obtiene la foto de perfil
        this._msGraphService.getPhoto()
          .pipe(takeUntil(this._unsubscribeAll))
          .subscribe(resp => {
            blobToBase64(resp).then(value => {
              Promise.resolve().then(() => {
                this.profilePhoto = value;
                this.refresh();
              });
            });
          });
      }
    });

    this._log.traceInfo(
      environment.projects.Bix,
      `Ingreso a BIX`,
      '',
      this._authenticationService.currentUserValue
    );

    // Mantiene sincronizado el ancho del panel acoplado con el control de la cabecera
    this.dockWidth = this._dockService.width;
    this._dockService.width$.pipe(takeUntil(this._unsubscribeAll)).subscribe(w => (this.dockWidth = w));

    // Repinta con cada cambio de la conversación, incluida una respuesta que
    // empezó en otra vista y sigue llegando
    this._session.attach();
    this._session.changes$.pipe(takeUntil(this._unsubscribeAll)).subscribe(change => {
      this.refresh();
      if (change.scroll) this.scrollToBottom();
    });

    this.scrollToBottom();
  }

  /**
   * OnDestroy
   */
  ngOnDestroy(): void {
    this.destroyed = true;

    // El stream no se corta: la respuesta sigue llegando a la sesión y queda
    // en el hilo para la próxima vista que se abra
    this._session.detach();

    // Unsubscribe for all subscriptions
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  // ─── Envío y streaming ──────────────────────────────────────────────
  //
  // El turno lo ejecuta ChatSessionService: si esta vista se destruye a mitad
  // de la respuesta, el stream sigue y la próxima vista la encuentra en el hilo.

  /**
   * Envía el mensaje al agente.
   * @param {string} message Texto del mensaje que escribe el usuario
   */
  sendMessage(message: string): Promise<void> {
    return this._session.sendMessage(message);
  }

  /**
   * Corta la respuesta que se está escribiendo.
   */
  stopStreaming(): void {
    this._session.stopStreaming();
  }

  /**
   * Responde a una solicitud de aprobación de una herramienta MCP y retoma el stream.
   * @param {ChatMessage} item Mensaje que contiene la solicitud
   * @param {boolean} approved Indica si el usuario autorizó la ejecución
   */
  respondApproval(item: ChatMessage, approved: boolean): Promise<void> {
    return this._session.respondApproval(item, approved);
  }

  /**
   * Reintenta la acción de recuperación que trae el error, si la hay.
   */
  retry(): void {
    this._session.retry();
  }

  /**
   * Descarta el error sin reintentar.
   */
  dismissError(): void {
    this._session.dismissError();
  }

  // ─── Feedback ───────────────────────────────────────────────────────

  /**
   * Registra un "me sirvió" sobre una respuesta.
   * @param {ChatMessage} item Mensaje valorado
   */
  like(item: ChatMessage): void {
    if (item.feedbackSent) return;

    item.feedback = 'like';
    item.feedbackOpen = false;
    this._session.saveFeedback(item, 'like', '');
  }

  /**
   * Abre la caja de comentario para explicar qué falló en la respuesta.
   * @param {ChatMessage} item Mensaje valorado
   */
  dislike(item: ChatMessage): void {
    if (item.feedbackSent) return;

    item.feedback = 'dislike';
    item.feedbackOpen = true;
  }

  /**
   * Envía el comentario escrito junto al "no me sirvió".
   * @param {ChatMessage} item Mensaje valorado
   */
  submitFeedback(item: ChatMessage): void {
    item.feedbackOpen = false;
    this._session.saveFeedback(item, 'dislike', item.feedbackText ?? '');
  }

  /**
   * Cancela el comentario y deja la respuesta sin valorar.
   * @param {ChatMessage} item Mensaje valorado
   */
  cancelFeedback(item: ChatMessage): void {
    item.feedbackOpen = false;
    item.feedback = undefined;
    item.feedbackText = '';
  }

  // ─── Conversación ───────────────────────────────────────────────────

  /**
   * Borra la conversación: la saca de la vista, del historial y del agente.
   */
  clearChat(): Promise<void> {
    return this._session.clearChat();
  }

  /**
   * Empieza una conversación nueva. La anterior no se pierde: ya quedó
   * guardada en el historial y se puede volver a abrir desde el panel.
   */
  newChat(): void {
    this._session.newChat();
  }

  // ─── Agente ─────────────────────────────────────────────────────────
  //
  // Solo en desarrollo hay más de un despliegue de Bryant para elegir.

  /** Despliegues del agente entre los que se puede elegir */
  get agentOptions(): ChatAgentOption[] { return this._agentService.options; }
  /** Despliegue activo */
  get currentAgent(): ChatAgentOption { return this._agentService.current; }
  /** Hay más de un despliegue: se dibuja el selector */
  get hasAgentChoice(): boolean { return this._agentService.hasChoice; }
  /** No es el despliegue por omisión de este ambiente: la barra se tiñe */
  get isSecondaryAgent(): boolean { return this._agentService.isSecondary; }

  /**
   * Cambia de despliegue del agente. Si había conversación, sigue en una nueva.
   * @param {ChatAgentOption} option Despliegue elegido
   */
  selectAgent(option: ChatAgentOption): void {
    this._session.switchAgent(option.key);
  }

  // ─── Historial ──────────────────────────────────────────────────────

  /**
   * Abre una conversación guardada.
   * @param {string} id Uuid de la conversación
   */
  openChat(id: string): Promise<void> {
    return this._session.openChat(id);
  }

  /**
   * Elimina una conversación del historial, previa confirmación.
   * @param {ChatSummary} chat Conversación a eliminar
   */
  async deleteChat(chat: ChatSummary): Promise<void> {
    if (!this.email || !chat?.id) return;

    const result = await this._swalService.showDeleteQuestion(chat.title);

    if (!result?.isConfirmed) {
      this._log.traceInfo(
        environment.projects.Bix,
        `Canceló la eliminación de una conversación`,
        chat.id,
        this._authenticationService.currentUserValue
      );
      return;
    }

    await this._session.deleteChat(chat);
  }

  /**
   * Guarda el nombre nuevo que el panel reportó para una conversación.
   * @param {{ chat: ChatSummary; title: string }} change Conversación y nombre nuevo
   */
  renameChat(change: { chat: ChatSummary; title: string }): Promise<void> {
    return this._session.renameChat(change.chat, change.title);
  }

  /**
   * Muestra u oculta el panel de conversaciones.
   */
  toggleHistory(): void {
    this.historyOpen = !this.historyOpen;
  }

  /**
   * Indica si el panel de conversaciones tiene que existir. Solo se ofrece en
   * pantalla completa: en el panel acoplado no hay ancho para una segunda columna.
   */
  get canShowHistory(): boolean {
    return this.isPage && !!this.email;
  }

  // ─── Adjuntos ───────────────────────────────────────────────────────

  /**
   * Valida y convierte a data uri los archivos elegidos por el usuario.
   * @param {Event} event Evento del input de archivos
   */
  async onFilesSelected(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);

    if (files.length === 0) return;

    await this.addFiles(files);

    // Permite volver a elegir el mismo archivo después de quitarlo
    input.value = '';
  }

  /**
   * Adjunta una captura de lo que el usuario está viendo, para poder preguntar
   * sobre la pantalla sin tener que describirla.
   *
   * Se captura con la api del navegador y no dibujando el dom: así entra lo
   * que vive dentro de un iframe, como los reportes de Power BI, que de la
   * otra forma saldrían en blanco. El navegador muestra su propio selector,
   * así que es el usuario el que elige qué comparte.
   */
  async attachScreenshot(): Promise<void> {
    // Se valida antes de abrir el selector para no hacer elegir una pantalla que se va a rechazar
    if (this.attachLimitReached) {
      this.showAttachLimitMsg(1);
      return;
    }

    const devices = navigator.mediaDevices as any;

    if (!devices?.getDisplayMedia) {
      this._swalService.showToastMsg(
        this.label('Tu navegador no permite capturar la pantalla', 'Your browser cannot capture the screen'),
        '', 'info'
      );
      return;
    }

    let stream: MediaStream;

    try {
      stream = await devices.getDisplayMedia({
        video: { displaySurface: 'browser' },
        audio: false,
        // Chrome y Edge preseleccionan esta pestaña; el resto lo ignora
        preferCurrentTab: true,
      });
    } catch (error) {
      // Cerrar el selector del navegador es una decisión del usuario, no un fallo
      if (error instanceof DOMException && ['NotAllowedError', 'AbortError'].includes(error.name)) return;

      this._swalService.showToastMsg(
        this.label('No se pudo capturar la pantalla', 'The screen could not be captured'), '', 'error'
      );

      this._log.traceError(
        environment.projects.Bix,
        `Error al capturar la pantalla`,
        '', error,
        this._authenticationService.currentUserValue
      );
      return;
    }

    try {
      await this.addFiles([await this.grabFrame(stream)]);
    } catch (error) {
      this._swalService.showToastMsg(
        this.label('No se pudo capturar la pantalla', 'The screen could not be captured'), '', 'error'
      );

      this._log.traceError(
        environment.projects.Bix,
        `Error al procesar la captura de pantalla`,
        '', error,
        this._authenticationService.currentUserValue
      );
    } finally {
      // Sin esto el navegador deja el aviso de "estás compartiendo" encendido
      stream.getTracks().forEach(track => track.stop());
    }
  }

  /**
   * Toma un cuadro del stream de captura y lo deja como archivo de imagen.
   *
   * @param {MediaStream} stream Stream que devolvió el selector del navegador
   * @returns {Promise<File>} Devuelve la captura como archivo
   */
  private async grabFrame(stream: MediaStream): Promise<File> {
    const video = document.createElement('video');

    video.srcObject = stream;
    video.muted = true;

    await video.play();

    // El primer cuadro suele venir en negro, así que se espera al siguiente
    await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    // Una pantalla 4k entera no aporta más contexto que una de 1600 y sí pesa
    // mucho más, tanto para el límite del adjunto como para el modelo
    const scale = Math.min(1, this.SCREENSHOT_MAX_SIDE / Math.max(video.videoWidth, video.videoHeight));

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);

    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);

    video.pause();
    video.srcObject = null;

    // El png mantiene el texto nítido, que es lo que importa en un tablero,
    // pero puede pasarse del límite de 5mb del adjunto: ahí sirve el jpeg
    let blob = await this.toBlob(canvas, 'image/png');

    if (blob.size > 4.5 * 1024 * 1024) {
      blob = await this.toBlob(canvas, 'image/jpeg', 0.92);
    }

    const extension = blob.type === 'image/jpeg' ? 'jpg' : 'png';
    const stamp = new Date().toTimeString().slice(0, 8).replace(/:/g, '-');

    return new File([blob], `pantalla-${stamp}.${extension}`, { type: blob.type });
  }

  /**
   * Envuelve `canvas.toBlob`, que trabaja con callback.
   *
   * @param {HTMLCanvasElement} canvas Lienzo con la captura ya dibujada
   * @param {string} type Tipo mime de salida
   * @param {number} quality Calidad, solo la usa el jpeg
   * @returns {Promise<Blob>} Devuelve la imagen codificada
   */
  private toBlob(canvas: HTMLCanvasElement, type: string, quality?: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => (blob ? resolve(blob) : reject(new Error(`No se pudo codificar la captura como ${type}`))),
        type,
        quality
      );
    });
  }

  /**
   * Valida y convierte a data uri un grupo de archivos, sin importar si los
   * eligió el usuario o salieron de una captura.
   *
   * @param {File[]} files Archivos a adjuntar
   */
  private async addFiles(files: File[]): Promise<void> {
    const count = validateFileCount(files, this.pendingFiles.length);

    if (!count.valid) {
      this.showAttachLimitMsg(files.length);
      return;
    }

    try {
      const converted = await convertFilesToDataUris(files);
      this.pendingFiles = [...this.pendingFiles, ...converted];
    } catch (error) {
      this._swalService.showInformationMsg(error instanceof Error ? error.message : String(error));
    } finally {
      this.refresh();
    }
  }

  /**
   * Avisa que se superó el máximo de adjuntos por mensaje.
   * @param {number} adding Cantidad de archivos que se intentaba agregar
   */
  private showAttachLimitMsg(adding: number): void {
    const params = { max: MAX_FILE_COUNT, current: this.pendingFiles.length, adding };

    this._translateService.get(['BELIVPEDIA.MESSAGES.BIX_MAX_ATTACHMENTS'], params)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe(value => {
        this._swalService.showInformationMsg(value['BELIVPEDIA.MESSAGES.BIX_MAX_ATTACHMENTS']);
      });
  }

  /**
   * Quita un adjunto que todavía no se envió.
   * @param {number} index Posición del adjunto
   */
  removePendingFile(index: number): void {
    this.pendingFiles = this.pendingFiles.filter((_, i) => i !== index);
  }

  /**
   * Icono que representa al adjunto según su tipo.
   * @param {string} mimeType Tipo mime del archivo
   * @returns {string} Devuelve el nombre del icono de feather
   */
  fileIcon(mimeType: string): string {
    if (mimeType?.startsWith('image/')) return 'image';
    if (mimeType === 'application/pdf') return 'file-text';

    return 'file';
  }

  /**
   * Tamaño del adjunto en un formato legible.
   * @param {number} bytes Tamaño en bytes
   * @returns {string} Devuelve el tamaño con su unidad
   */
  fileSize(bytes: number): string {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;

    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // ─── Fuentes citadas ────────────────────────────────────────────────

  /**
   * Abre un archivo citado en un popup, sin sacar al usuario de la conversación.
   * @param {string} fileId Indica el id del archivo en la belivpedia
   */
  showFile(fileId: string): void {
    const modal = this._ngbModalService.open(ChatFilePreviewModalComponent, {
      size: 'xl',
      centered: true,
      animation: true,
      windowClass: 'bl-preview-modal',
      backdrop: true
    });

    modal.componentInstance.fileId = fileId;

    this._log.traceInfo(
      environment.projects.Belivpedia,
      `Abrió un documento citado por Bryant en un popup`,
      fileId,
      this._authenticationService.currentUserValue
    );
  }

  /**
   * Intercepta los clics sobre los enlaces de una respuesta. Si el enlace es
   * de esta misma app, navega con el router (sin recargar el portal); si es de
   * otra app, lo abre en una pestaña nueva.
   *
   * Ctrl/Cmd/Shift + clic y el clic del medio se dejan al navegador, para que
   * el usuario pueda seguir abriendo el enlace donde quiera.
   *
   * @param {MouseEvent} event Clic dentro del markdown del mensaje
   */
  onMessageLinkClick(event: MouseEvent): void {
    const anchor = (event.target as HTMLElement)?.closest?.('a[href]') as HTMLAnchorElement;

    if (!anchor || event.defaultPrevented) return;
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

    let url: URL;
    try {
      url = new URL(anchor.getAttribute('href'), window.location.href);
    } catch {
      return;
    }

    // mailto:, tel:, etc. siguen su comportamiento normal
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return;

    event.preventDefault();

    if (url.origin !== window.location.origin) {
      this._log.traceInfo(
        environment.projects.Bix,
        `Abrió un enlace externo desde una respuesta de Bryant`,
        url.origin,
        this._authenticationService.currentUserValue
      );

      window.open(url.href, '_blank', 'noopener');
      return;
    }

    const target = `${url.pathname}${url.search}${url.hash}`;

    this._log.traceInfo(
      environment.projects.Bix,
      `Navegó a una pantalla del portal desde una respuesta de Bryant`,
      url.pathname,
      this._authenticationService.currentUserValue
    );

    // Desde la página completa del chat, la conversación sigue a mano en el
    // panel acoplado mientras el usuario mira la pantalla a la que fue
    if (this.isPage) {
      this._dockService.open();
    }

    this._router.navigateByUrl(target).catch(ex => {
      this._log.traceError(
        environment.projects.Bix,
        `No se pudo navegar a un enlace de una respuesta de Bryant`,
        url.pathname,
        ex,
        this._authenticationService.currentUserValue
      );
    });
  }

  /**
   * Muestra el fragmento del documento en el que se apoyó la respuesta.
   * @param {string} content Indica el contenido de la cita
   */
  showQuote(content: string): void {
    const modal = this._ngbModalService.open(ChatQuoteModalComponent, {
      backdrop: 'static',
      size: 'lg',
      centered: true,
      animation: true,
      windowClass: 'modal quote-modal',
      keyboard: false
    });

    modal.componentInstance.title = 'BELIVPEDIA.MESSAGES.VIEW_QUOTE';
    modal.componentInstance.content = content;
  }

  /**
   * Paso de razonamiento que el agente está ejecutando ahora mismo. Es el
   * último que abrió y todavía no cerró; si ya cerró todos, el más reciente.
   *
   * @param {ChatMessage} item Mensaje del asistente
   * @returns {ReasoningStep} Devuelve el paso en curso, o undefined si no hay
   */
  liveStep(item: ChatMessage): ReasoningStep {
    const steps = item?.reasoning;

    if (!steps?.length) return undefined;

    return [...steps].reverse().find(s => !s.done) ?? steps[steps.length - 1];
  }

  // ─── Fecha de los mensajes ──────────────────────────────────────────

  /**
   * Cuándo se envió o se recibió el mensaje, en formato corto: solo la hora si
   * fue hoy, el día y la hora si es de otra fecha.
   *
   * @param {ChatMessage} item Mensaje del hilo
   * @returns {string} Devuelve la fecha lista para mostrar, vacía si no la hay
   */
  messageTime(item: ChatMessage): string {
    const date = this.timeOf(item);

    if (!date) return '';

    const locale = this.locale;
    const hour = date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });

    if (date.toDateString() === new Date().toDateString()) return hour;

    return `${date.toLocaleDateString(locale, { day: '2-digit', month: 'short' })} · ${hour}`;
  }

  /**
   * Fecha y hora completa del mensaje, para el tooltip.
   * @param {ChatMessage} item Mensaje del hilo
   * @returns {string} Devuelve la fecha completa, vacía si no la hay
   */
  messageTimeFull(item: ChatMessage): string {
    const date = this.timeOf(item);

    if (!date) return '';

    return date.toLocaleString(this.locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  /**
   * Momento en el que se registró el mensaje.
   * @param {ChatMessage} item Mensaje del hilo
   * @returns {Date} Devuelve la fecha, o null si no es válida
   */
  private timeOf(item: ChatMessage): Date | null {
    if (!item?.more?.time) return null;

    const date = new Date(item.more.time);

    return isNaN(date.getTime()) ? null : date;
  }

  /**
   * Locale con el que se formatean las fechas del hilo.
   */
  private get locale(): string {
    return this._translateService.currentLang === 'en' ? 'en-US' : 'es-CL';
  }

  // ─── Copiar al portapapeles ─────────────────────────────────────────

  /**
   * Copia una respuesta suelta, con sus fuentes.
   *
   * @param {ChatMessage} item Mensaje a copiar
   */
  async copyMessage(item: ChatMessage): Promise<void> {
    const copied = await this.copyToClipboard(this.buildMessage(item));

    if (!copied) {
      this._log.traceError(
        environment.projects.Belivpedia,
        `No se pudo copiar una respuesta de Bryant`,
        item.id,
        null,
        this._authenticationService.currentUserValue
      );
      this._swalService.showToastMsg(
        this.label('No se pudo copiar la respuesta', 'The answer could not be copied'), '', 'error'
      );
      return;
    }

    this._log.traceInfo(
      environment.projects.Belivpedia,
      `Copió una respuesta de Bryant`,
      item.id,
      this._authenticationService.currentUserValue
    );

    // El botón confirma solo, sin robarle la atención al usuario con un aviso
    this.copiedMessageId = item.id;
    this.refresh();

    setTimeout(() => {
      if (this.copiedMessageId === item.id) {
        this.copiedMessageId = null;
        this.refresh();
      }
    }, 1600);
  }

  /**
   * Arma el mensaje como HTML, para que al pegarlo en Word, Outlook o Teams
   * llegue con formato (negritas, listas, tablas) y no como markdown crudo:
   * qué adjuntó, qué dijo y en qué fuentes se apoyó.
   *
   * @param {ChatMessage} item Mensaje a convertir
   * @returns {HTMLElement} Devuelve un contenedor suelto con el mensaje
   */
  private buildMessage(item: ChatMessage): HTMLElement {
    const container = document.createElement('div');

    (item.attachments ?? []).forEach(attachment => {
      const paragraph = document.createElement('p');

      paragraph.textContent = `[${attachment.fileName}]`;
      container.appendChild(paragraph);
    });

    // Se copia el contenido original, no el que quedó con los marcadores de
    // cita metidos en el markdown. Lo que devuelve el agente pasa por el
    // sanitizador antes de tocar el DOM.
    if (item.content) {
      const html = marked.parse(item.content.trim(), { async: false }) as string;
      const body = document.createElement('div');

      body.innerHTML = this._sanitizer.sanitize(SecurityContext.HTML, html) ?? '';
      container.appendChild(body);
    }

    if (item.citations?.length) {
      const title = document.createElement('p');
      const strong = document.createElement('strong');
      const list = document.createElement('ol');

      strong.textContent = `${this.label('Fuentes', 'Sources')}:`;
      title.appendChild(strong);
      container.appendChild(title);

      item.citations.forEach(citation => {
        const entry = document.createElement('li');

        entry.value = citation.index;

        if (citation.url) {
          const link = document.createElement('a');

          link.href = citation.url;
          link.textContent = citation.label;
          entry.appendChild(link);
        } else {
          entry.textContent = citation.label;
        }

        list.appendChild(entry);
      });

      container.appendChild(list);
    }

    // Word y Outlook no dibujan bordes en las tablas si no vienen en línea
    container.querySelectorAll('table').forEach(table => {
      table.style.borderCollapse = 'collapse';
    });
    container.querySelectorAll('th, td').forEach((cell: HTMLElement) => {
      cell.style.border = '1px solid #c8c8c8';
      cell.style.padding = '4px 8px';
    });

    return container;
  }

  /**
   * Escribe el mensaje en el portapapeles como texto enriquecido, con su
   * versión en texto plano para los destinos que no aceptan HTML.
   *
   * @param {HTMLElement} content Contenedor con el mensaje
   * @returns {Promise<boolean>} Devuelve si se pudo copiar
   */
  private async copyToClipboard(content: HTMLElement): Promise<boolean> {
    // Se monta fuera de la vista: innerText necesita el layout para devolver
    // los saltos de línea, y el camino viejo necesita poder seleccionarlo
    content.style.position = 'fixed';
    content.style.top = '0';
    content.style.left = '-9999px';
    content.style.whiteSpace = 'normal';
    document.body.appendChild(content);

    try {
      const html = content.innerHTML;
      const text = content.innerText.trim();

      try {
        if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': new Blob([html], { type: 'text/html' }),
              'text/plain': new Blob([text], { type: 'text/plain' })
            })
          ]);

          return true;
        }
      } catch {
        // El api del portapapeles pide contexto seguro y permiso: si lo niega,
        // todavía queda el camino viejo
      }

      try {
        const selection = window.getSelection();
        const range = document.createRange();

        range.selectNodeContents(content);
        selection.removeAllRanges();
        selection.addRange(range);

        // Copiar una selección del DOM lleva el formato, igual que el api nuevo
        const copied = document.execCommand('copy');

        selection.removeAllRanges();

        return copied;
      } catch {
        return false;
      }
    } finally {
      document.body.removeChild(content);
    }
  }

  /**
   * Abre o cierra el detalle del razonamiento de una respuesta.
   * @param {ChatMessage} item Mensaje del asistente
   */
  toggleReasoning(item: ChatMessage): void {
    item.reasoningOpen = !item.reasoningOpen;
  }

  // ─── Compositor y layout ────────────────────────────────────────────

  /**
   * Indica si hay un turno en curso.
   */
  get isBusy(): boolean {
    return this._session.isBusy;
  }

  /**
   * Indica si hay que pintar el indicador de escritura suelto, el que no
   * pertenece a ningún mensaje. Solo cubre el hueco entre que se envía el
   * mensaje y el agente da su primera señal: en cuanto hay razonamiento o
   * texto, el progreso lo muestra la propia burbuja de la respuesta.
   */
  get showTypingBubble(): boolean {
    if (this.status === 'sending') return true;

    const item = this.streamingMessage;

    return !!item && !item.content && !item.reasoning?.length;
  }

  /**
   * Indica si el chat se está mostrando dentro del panel acoplado.
   */
  get isDock(): boolean {
    return this.variant === 'dock';
  }

  /**
   * Indica si el chat se está mostrando en su página completa.
   */
  get isPage(): boolean {
    return this.variant === 'page';
  }

  /**
   * Indica si hay algo que enviar (texto o adjunto).
   */
  get canSend(): boolean {
    return !this.isBusy && ((this.draft || '').trim().length > 0 || this.pendingFiles.length > 0);
  }

  /**
   * Mensaje que está recibiendo tokens en este momento.
   */
  get streamingMessage(): ChatMessage | undefined {
    return this._session.streamingMessage;
  }

  /**
   * Envía el borrador actual y limpia la caja de texto.
   */
  submitDraft(): void {
    if (!this.canSend) return;

    const message = (this.draft || '').trim();
    this.draft = '';
    this.sendMessage(message);
  }

  /**
   * Enter envía, Shift+Enter agrega salto de línea.
   * @param {KeyboardEvent} event Evento del teclado
   */
  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.submitDraft();
    }
  }

  /**
   * Ajusta el alto de la caja de texto al contenido, hasta un máximo.
   * @param {HTMLTextAreaElement} textarea Caja de texto del compositor
   */
  autoGrow(textarea: HTMLTextAreaElement): void {
    if (!textarea) return;

    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 140)}px`;
  }

  /**
   * Usa uno de los prompts sugeridos y lo envía.
   * @param {string} prompt Texto del prompt
   */
  usePrompt(prompt: string): void {
    this.draft = '';
    this.sendMessage(prompt);
  }

  /**
   * Función que sirve para enviar el scroll hasta abajo del formulario
   */
  scrollToBottom(): void {
    // Durante el streaming esto se llama por cada chunk, así que se agenda un
    // único ajuste por tick en lugar de uno por token recibido
    if (this.scrollPending) return;

    this.scrollPending = true;

    setTimeout(() => {
      this.scrollPending = false;
      this.scrolltop = this.scrollMe?.nativeElement.scrollHeight;
    }, 0);
  }

  /**
   * Función para ocultar el chat
   */
  hide(): void {
    this.hideChat.emit(true);
  }

  /**
   * Lleva la conversación a la página completa y cierra el panel acoplado.
   */
  expandToPage(): void {
    this._dockService.close();
    this._router.navigate(['/pages/chat']);
  }

  /**
   * Regresa la conversación al panel acoplado desde la página completa.
   */
  collapseToDock(): void {
    this._dockService.open();

    // Devuelve al usuario a donde estaba antes de abrir la página del chat
    if (window.history.length > 1) {
      window.history.back();
    } else {
      this._router.navigate(['/']);
    }
  }

  /**
   * Cambia cuánto de la pantalla ocupa el panel acoplado.
   * @param {number} preset Ancho en % del viewport
   */
  setDockWidth(preset: number): void {
    this._dockService.setWidth(preset);
  }

  /**
   * Cierra el chat según el contexto donde se esté mostrando.
   */
  closeChat(): void {
    if (this.isDock) {
      this._dockService.close();
      return;
    }

    this.hideChat.emit(true);
  }

  /**
   * Etiqueta localizada sin depender de claves nuevas del backend de traducciones.
   * @param {string} es Texto en español
   * @param {string} en Texto en inglés
   * @returns {string} Texto según el idioma activo
   */
  label(es: string, en: string): string {
    return this._translateService.currentLang === 'en' ? en : es;
  }

  /**
   * Identidad para el *ngFor del hilo, evita repintar burbujas que no cambiaron.
   * @param {number} _index Posición en el hilo
   * @param {ChatMessage} item Mensaje del hilo
   * @returns {string} Devuelve el id del mensaje
   */
  trackByMessage(_index: number, item: ChatMessage): string {
    return item.id;
  }

  /**
   * Refresca la vista. El stream resuelve fuera de los eventos habituales de
   * Angular, así que cada acción necesita pedir el repintado a mano.
   */
  private refresh(): void {
    if (this.destroyed) return;

    this._cdr.detectChanges();
  }
}
